import {
  InfiniteData,
  QueryKey,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useApiClient } from '../contexts/ApiContext';
import {
  ApiCreatePayoutExternalTransferRequest,
  ApiCreatePayoutInternalTransferRequest,
  ApiCreatePayoutRequest,
  ApiError,
  ApiExecutePayoutRequest,
  ApiPayoutListResponse,
  ApiUpdatePayoutRequest,
} from '../openapi';
import { downloadUrl } from '@razz-team/utils';
import { mapResponse } from './helpers';

export function usePayoutsInfiniteQuery() {
  const client = useApiClient();
  return useInfiniteQuery<
    ApiPayoutListResponse,
    ApiError,
    InfiniteData<ApiPayoutListResponse, string | undefined>,
    QueryKey,
    string | undefined
  >({
    queryKey: ['payouts'],
    queryFn: ({ pageParam, signal }) =>
      client.GET('/payouts', { signal, params: { query: { page: pageParam } } }).then(mapResponse),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage?.nextPage,
  });
}

export function usePayoutQuery({ payoutId, createdAt }: { payoutId: string; createdAt: string }) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['payouts', payoutId, createdAt],
    queryFn: () =>
      client
        .GET('/payouts/{id}', { params: { path: { id: payoutId }, query: { createdAt } } })
        .then(mapResponse),
    refetchInterval: (query) => (query.state.data?.payout.state === 'STARTED' ? 5000 : false),
  });
}

export function useCreatePayoutMutation({ idempotencyKey }: { idempotencyKey: string }) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['payouts', 'create', idempotencyKey],
    mutationFn: (body: ApiCreatePayoutRequest) =>
      api
        .POST('/payouts', {
          body,
          headers: {
            'Idempotency-Key': idempotencyKey,
          },
        })
        .then(mapResponse),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payouts'] }),
  });
}

export function useUpdatePayoutMutation({
  payoutId,
  createdAt,
  idempotencyKey,
}: {
  payoutId: string;
  createdAt: string;
  idempotencyKey: string;
}) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['payouts', payoutId, createdAt, 'update', idempotencyKey],
    mutationFn: (body: ApiUpdatePayoutRequest) =>
      api
        .PATCH('/payouts/{id}', {
          body,
          params: {
            path: { id: payoutId },
            query: { createdAt } as never,
          },
          headers: {
            'Idempotency-Key': idempotencyKey,
          },
        })
        .then(() => null),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payouts'] }),
  });
}

export function useExecutePayoutMutation({
  payoutId,
  createdAt,
  idempotencyKey,
}: {
  payoutId: string;
  createdAt: string;
  idempotencyKey: string;
}) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['payouts', payoutId, createdAt, 'start', idempotencyKey],
    mutationFn: (body: ApiExecutePayoutRequest) =>
      api
        .POST('/payouts/{id}/execute', {
          body,
          params: {
            path: { id: payoutId },
            query: { createdAt },
          },
          headers: {
            'Idempotency-Key': idempotencyKey,
          },
        })
        .then(mapResponse),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payouts'] }),
  });
}

export function useCancelPayoutMutation({ idempotencyKey }: { idempotencyKey: string }) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['payouts', 'cancel', idempotencyKey],
    mutationFn: ({ payoutId, createdAt }: { payoutId: string; createdAt: string }) =>
      api
        .POST('/payouts/{id}/cancel', {
          params: {
            path: { id: payoutId },
            query: { createdAt },
          },
          headers: {
            'Idempotency-Key': idempotencyKey,
          },
        })
        .then(mapResponse),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payouts'] }),
  });
}

export function useCreatePayoutInternalTransferMutation({
  payoutId,
  createdAt,
  idempotencyKey,
}: {
  payoutId: string;
  createdAt: string;
  idempotencyKey: string;
}) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['payouts', payoutId, createdAt, 'transfers', 'internal', idempotencyKey],
    mutationFn: (body: ApiCreatePayoutInternalTransferRequest) =>
      api
        .POST('/payouts/{id}/transfers/internal', {
          body,
          params: {
            path: { id: payoutId },
            query: { createdAt } as never,
          },
          headers: {
            'Idempotency-Key': idempotencyKey,
          },
        })
        .then(mapResponse),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payouts'] }),
  });
}

export function useCreatePayoutExternalTransferMutation({
  payoutId,
  createdAt,
  idempotencyKey,
}: {
  payoutId: string;
  createdAt: string;
  idempotencyKey: string;
}) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['payouts', payoutId, createdAt, 'transfers', 'externals', idempotencyKey],
    mutationFn: (body: ApiCreatePayoutExternalTransferRequest) =>
      api
        .POST('/payouts/{id}/transfers/external', {
          body,
          params: {
            path: { id: payoutId },
            query: { createdAt } as never,
          },
          headers: { 'Idempotency-Key': idempotencyKey },
        })
        .then(mapResponse),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payouts'] }),
  });
}

export function usePayoutStatementTransfersMutation({
  payoutId,
  createdAt,
}: {
  payoutId: string;
  createdAt: string;
}) {
  const api = useApiClient();
  return useMutation({
    mutationKey: ['payouts', payoutId, 'statement', 'transfers', createdAt],
    mutationFn: () =>
      api
        .GET('/payouts/{id}/statement/transfers', {
          // TODO: remove createdAt from path to query in API
          params: { path: { id: payoutId, createdAt }, query: { createdAt } as never },
        })
        .then(({ data }) => {
          if (data?.link) {
            downloadUrl(data.link);
          }
        }),
  });
}
